import { ComponentIds, Entity, ProductUtils, SelectedComponent } from '@inter-ikea-core/core';
import {
  Product,
  ProductType,
  SelectionOverrideComponent,
  SlidingDoorComponent
} from '@inter-ikea-storageone/range';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useCore } from 'contexts/core';
import { useWidget } from 'contexts/widget';
import { useAppDispatch } from 'ducks/hooks';
import {
  getConfigSorting,
  getFilterState,
  getMarketProducts,
  getProductsInScene,
  getSelectedGroupId
} from 'ducks/products';
import { setShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import * as R from 'ramda';
import addInteriorSuggestion from 'rangeConfig/pax/behaviors/addInteriorSuggestion';
import { sceneHasSingleFrameGroup } from 'rangeConfig/shared/utils/inScene';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import InteriorSuggestionsDesktop from '../../../shared/widgets/interiorSuggestions/InteriorSuggestionsDesktop';
import InteriorSuggestionsMobile from '../../../shared/widgets/interiorSuggestions/InteriorSuggestionsMobile';
import InteriorSuggestionsMultiFrameList from './InteriorSuggestionsMultiFrameList';
import SelectInSceneImage from './images/select_pax_frame_in_scene.png';
import { store } from 'ducks';
import { BatchType, ProductBatch } from 'utils/batch';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/shared/widgets/interiorSuggestions/noneOptionForInteriorSuggestions';

export const InteriorSuggestionsFrameTypes = [
  ProductType.PAX_FRAME,
  ProductType.PAX_CORNER_SOLUTION_ASM
];
export const InteriorSuggestionTypes = [ProductType.PAX_INTERIOR_SUGGESTIONS];

export default function InteriorSuggestions() {
  const dispatch = useAppDispatch();
  const rootState = store.getState();
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const productsInScene = useSelector(getProductsInScene);
  const entityManager = coreApi.getServices().entityManager;
  const filterState = useSelector(getFilterState);
  const { configId } = useWidget();
  const sorting = useSelector(getConfigSorting(configId));
  const selectedGroupId = useSelector(getSelectedGroupId);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);

  const interiorSuggestionProducts = useMemo(() => {
    return products.filter(p => InteriorSuggestionTypes.includes(p.type));
  }, [products]);

  const frameProducts = useMemo(() => {
    return products.filter(p => InteriorSuggestionsFrameTypes.includes(p.type));
  }, [products]);

  let suggestionsForSelectedFrame: Product[] = [];

  const optionsMap: Record<string, ProductType[]> = filterState(selectedGroupId, 'type');

  const selectedOptionsTypes = Object.values(optionsMap).filter(o => o.length > 0);

  if (selectedEntity) {
    const frameProduct = frameProducts.find(
      p => p.coreProductId === ProductUtils.productRef(selectedEntity)
    );

    const hingedDoorsOnFrame = coreApi.entities
      .getFrameDescendants(selectedEntity)
      .filter(
        entity =>
          entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID) &&
          !entity.hasComponent(SlidingDoorComponent.id)
      );

    suggestionsForSelectedFrame = interiorSuggestionProducts.filter(p => {
      if (frameProduct && frameProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
        const regularFrame = frameProduct.parts.find(
          part => part.product?.type === ProductType.PAX_FRAME
        );
        return regularFrame?.product
          ? p.meta?.applicableFrameTypes?.includes('cornerRegularFrame') &&
              R.equals(regularFrame.product?.communicatedDimensions, p.communicatedDimensions) &&
              p.design === (filterState(selectedGroupId, 'design').design || frameProduct.design)
          : false;
      } else {
        return frameProduct
          ? R.equals(p.communicatedDimensions, frameProduct.communicatedDimensions) &&
              p.design === (filterState(selectedGroupId, 'design').design || frameProduct.design)
          : false;
      }
    });

    suggestionsForSelectedFrame = suggestionsForSelectedFrame.filter(product => {
      const partTypes = product.parts.flatMap(part => part.product?.type || []);
      const allOptionsSatisfied = selectedOptionsTypes.every(selectedTypes =>
        selectedTypes.find(type => partTypes.includes(type))
      );
      return allOptionsSatisfied;
    });

    suggestionsForSelectedFrame = suggestionsForSelectedFrame.filter(product => {
      if (!product.meta || !product.meta.applicableFrameTypes) {
        return true;
      }

      const hasHinged = product.meta.applicableFrameTypes.includes('hinged');
      const hasNonHinged = product.meta.applicableFrameTypes.includes('non-hinged');

      if (hasHinged && hasNonHinged) {
        return true;
      }

      return hingedDoorsOnFrame.length > 0 ? hasHinged : hasNonHinged;
    });

    if (sorting?.compare)
      suggestionsForSelectedFrame.sort(ProductBatch.comparison(sorting.compare, BatchType.Product));
  }

  const getFramesInScene = useCallback(() => {
    const framesInScene = coreApi.entities
      .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
      .filter(f => !f.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID));
    const cornerSolutionsInScene = coreApi.entities.getByProductType(
      ProductType.PAX_CORNER_SOLUTION_ASM
    );
    return [...framesInScene, ...cornerSolutionsInScene];
  }, [coreApi.entities]);

  const addSelectionOverrideComponentToFrames = useCallback(() => {
    const framesInScene = getFramesInScene();
    framesInScene.forEach(frame => frame.addComponent(new SelectionOverrideComponent()));
    if (framesInScene.length === 1) {
      // If only one frame in scene, select it
      framesInScene[0].addComponent(new SelectedComponent());
      setSelectedEntity(framesInScene[0]);
    }
  }, [getFramesInScene]);

  const removeSelectionOverrideComponentFromFrames = useCallback(() => {
    const framesInScene = getFramesInScene();
    framesInScene.forEach(frame => frame.removeComponent(SelectionOverrideComponent.id));
  }, [getFramesInScene]);

  useEffect(() => {
    addSelectionOverrideComponentToFrames();
    dispatch(setShowSelectionOverrideIndicators(true));
    return () => {
      removeSelectionOverrideComponentFromFrames();
      dispatch(setShowSelectionOverrideIndicators(false));
      coreApi.entities.unselect();
    };
  }, [
    addSelectionOverrideComponentToFrames,
    coreApi,
    dispatch,
    removeSelectionOverrideComponentFromFrames
  ]);

  useEffect(() => {
    addSelectionOverrideComponentToFrames();
  }, [productsInScene, addSelectionOverrideComponentToFrames]);

  useEffect(() => {
    const onSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(entity);
      }
    };
    const onUnSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(null);
      }
    };

    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    };
  }, [entityManager]);

  const handleClick = (product: Product) =>
    behaviorTracker.custom(() => {
      const props = {
        coreProductId: product.coreProductId,
        menu: 'main_menu'
      };

      addInteriorSuggestion({ coreApi, dispatch, rootState, props });
    });

  if (sceneHasSingleFrameGroup(coreApi) && !selectedEntity) {
    return <InteriorSuggestionsMultiFrameList />;
  }

  return isMobile() ? (
    <InteriorSuggestionsMobile
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={suggestionsForSelectedFrame}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  ) : (
    <InteriorSuggestionsDesktop
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={suggestionsForSelectedFrame}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  );
}
